import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, MessageSquare, BarChart2,
  Settings, LogOut, Menu, X, Activity, Building2, Shield
} from "lucide-react";
import { useState } from "react";
import NotificationBell from "@/components/NotificationBell";

const ROLE_LABELS = {
  secretario: "Secretário(a)", coordenador: "Coordenador(a) APS",
  enfermeiro: "Enfermeiro(a)", medico: "Médico(a)",
  tecnico: "Técnico(a) Enf.", acs: "ACS",
  especialista: "Especialista", admin: "Administrador"
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const managerRoles = ["admin", "secretario", "coordenador"];
  const isManager = managerRoles.includes(user?.role);
  const isSystemAdmin = user?.role === "admin";
  const isSpecialist = ["especialista", "medico", "admin"].includes(user?.role);

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/patients", icon: Users, label: "Pacientes" },
    { to: "/teleconsultation", icon: MessageSquare, label: "Interconsulta" },
    ...(isSpecialist ? [{ to: "/especialista", icon: Shield, label: "Painel Especialista" }] : []),
    ...(isManager ? [{ to: "/municipal", icon: BarChart2, label: "Painel Municipal" }] : []),
    ...(isManager ? [{ to: "/gestao-municipal", icon: Building2, label: "Acesso Município" }] : []),
    ...(isSystemAdmin ? [{ to: "/admin", icon: Settings, label: "Administração" }] : []),
  ];

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-secondary hover:text-foreground"
        }`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col fixed left-0 top-0 h-full w-60 bg-white border-r border-border z-40">
        {/* Logo */}
        <div className="p-5 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                <Activity className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground leading-tight" style={{ fontFamily: 'Manrope' }}>
                  RedeCicatriza
                </p>
                <p className="text-xs text-muted-foreground">Atenção Primária</p>
              </div>
            </div>
            <NotificationBell />
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(item => <NavItem key={item.to} {...item} />)}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-2.5 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="overflow-hidden">
              <p className="text-sm font-medium text-foreground truncate">{user?.full_name || user?.name}</p>
              <p className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role] || user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-all"
          >
            <LogOut className="w-4 h-4" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 md:ml-60 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-border flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              RedeCicatriza
            </span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-secondary transition-all"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </header>

        {/* Mobile menu overlay */}
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 z-20 bg-black/30"
            onClick={() => setMobileMenuOpen(false)}
          >
            <div
              className="absolute top-14 left-0 right-0 bg-white border-b border-border p-4 space-y-1"
              onClick={(e) => e.stopPropagation()}
            >
              {navItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
              <div className="border-t border-border pt-2 mt-2">
                <div className="flex items-center gap-2 px-3 py-2 mb-1">
                  {user?.picture && <img src={user.picture} className="w-7 h-7 rounded-full" alt="" />}
                  <div>
                    <p className="text-sm font-medium">{user?.full_name || user?.name}</p>
                    <p className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role]}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-lg"
                >
                  <LogOut className="w-4 h-4" />
                  Sair
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8">
          {children}
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-border z-30 flex">
          {navItems.slice(0, 4).map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center py-2.5 gap-1 text-xs font-medium transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
              data-testid={`bottom-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {({ isActive }) => (
                <>
                  <Icon className={`w-5 h-5 ${isActive ? "text-primary" : ""}`} />
                  <span className="text-[10px] leading-tight">{label.split(" ")[0]}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
